import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Grid, Typography, Divider, Hidden, LinearProgress } from '@material-ui/core';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import ContentWrapper from '@trdk/style/ContentWrapper';
import Button from '@trdk/style/Button';
import FileUpload from '../components/FileUpload';
import sendFiles from './send-file';
import ErrorTextBox from '../components/error-textbox';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import InputLabel from '@material-ui/core/InputLabel';
import FormControl from '@material-ui/core/FormControl';

const acceptedFileTypes = [
  '.csv',
  'text/csv',
  'application/pdf',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
];

const UnitIDMap = {
  Østbyen: 'OST',
  Heimdal: 'HEI',
  Omsorgsenheten: 'BOT',
  Lerkendal: 'LER',
  Midtbyen: 'MIT',
};

const SendWhitelist = () => {
  const classes = useStyles();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState('');

  async function onSubmit(values) {
    if (
      !values?.fileList?.length ||
      values.fileList.some(
        (file) => !acceptedFileTypes.find((fileType) => fileType === file.file.type)
      )
    ) {
      return;
    }

    setIsSubmitting(true);

    try {
      await sendFiles(values.fileList, selectedUnit);
      setSubmitted(true);
    } catch (error) {
      setSubmitError([...submitError, new Date()]);
    }

    setIsSubmitting(false);
  }

  return (
    <div>
      <Typography variant="h1" className={classes.header}>
        Filopplasting Modulus Barn
      </Typography>
      {!submitted ? (
        <Formik
          initialValues={{
            fileList: [],
          }}
          validationSchema={yup.object().shape({
            fileList: yup.array(),
          })}
          onSubmit={onSubmit}
        >
          {({ setFieldValue, values }) => (
            <Form>
              <ContentWrapper>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControl
                      size="small"
                      className={classes.formControl}
                      style={{ minWidth: '350px' }}
                    >
                      <InputLabel htmlFor="age-simple">
                        Velg en bydel å sende til (valgfritt)
                      </InputLabel>
                      <Select
                        autoWidth={true}
                        value={selectedUnit}
                        onChange={(event) => setSelectedUnit(event.target.value)}
                      >
                        {Object.entries(UnitIDMap).map(([unitName, unitID]) => (
                          <MenuItem key={unitName} value={unitID}>
                            {unitName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <Divider />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="body1">
                      Filene må være ukryptert, vi sørger for å håndtere dataen på riktig måte.
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="body1">
                      Det er kun mulig å sende inn filer på disse formatene:
                      <i>{' <csv, pdf, docx, doc, xls, xlsx>'}</i>
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <FileUpload
                      name="fileList"
                      files={values.fileList}
                      onUpdate={(fileItems) => setFieldValue('fileList', fileItems)}
                      acceptedFileTypes={acceptedFileTypes}
                      labelFileTypeNotAllowed="Feil filtype"
                      fileValidateTypeLabelExpectedTypes="Filen må være .csv, .pdf, .docx, .doc, .xls eller .xlsx"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Divider />
                  </Grid>

                  <Grid item xs={12}>
                    {submitError[submitError.length - 1] && (
                      <ErrorTextBox
                        message="Feil under innsending. Prøv igjen senere."
                        autoHideDuration={null}
                      />
                    )}
                  </Grid>
                  <Hidden xsDown>
                    <Grid item sm={6} md={8} />
                  </Hidden>
                  <Grid item xs={12} sm={6} md={4}>
                    <Button
                      fullWidth
                      color="primary"
                      variant="contained"
                      disabled={isSubmitting || values.fileList.length === 0}
                      type="submit"
                    >
                      Send inn
                    </Button>
                    {isSubmitting && <LinearProgress />}
                  </Grid>
                </Grid>
              </ContentWrapper>
            </Form>
          )}
        </Formik>
      ) : (
        <ContentWrapper>
          <Typography variant="h6">Filen(e) ble lastet opp til Modulus Barn.</Typography>
          <Typography variant="body1">
            Hvis du vil laste opp flere filer kan du trykke på "Ny filopplasting".
          </Typography>
          <Button
            color="primary"
            variant="outlined"
            className={classes.mt}
            onClick={() => setSubmitted(false)}
          >
            Ny filopplasting
          </Button>
        </ContentWrapper>
      )}
    </div>
  );
};

const useStyles = makeStyles({
  header: {
    fontSize: '2.2rem',
    padding: '1rem 0',
  },
  subheader: {
    fontSize: '1.1rem',
  },
  flex: {
    display: 'flex',
  },
  fullwidth: {
    width: '100%',
    marginTop: '1rem',
  },
  errorIcon: {
    marginRight: '0.5rem',
  },
  mt: {
    marginTop: '1rem',
  },
});

export default SendWhitelist;
